import type { CustomCellEditorProps } from 'ag-grid-react';
import { useMemo } from 'react';

import { useQuery } from '@mint-lib/query-client';
import { ProgressCircular } from '@mint-lib/tw-components/patterns';
import { Metadata } from '@mint-sdk/api-mdm';

import {
  FlatSelectEditor,
  type FlatSelectOption,
} from './FlatSelectEditor.jsx';

export interface RelatedSelectEditorProps extends CustomCellEditorProps<
  unknown,
  string | string[]
> {
  type: 'single' | 'multiple';
  relatedFieldId: string;
  companyId: number;
  mdmContext: string; // Context for the API request (e.g., 'planning')
  primaryEntitiesUrnMap: Record<string, string[]>;
}

export const RelatedSelectEditor = ({
  relatedFieldId,
  companyId,
  mdmContext,
  primaryEntitiesUrnMap,
  ...restProps
}: RelatedSelectEditorProps) => {
  const requestData = {
    companyId,
    context: mdmContext,
    relations: [
      {
        relatedFieldDefinitionUrnId: relatedFieldId,
        primaryEntitiesUrnMap,
      },
    ],
  };

  const { queryKey, ...queryOptions } =
    Metadata.getRelatedFieldsMapQueryOptions({
      data: requestData,
    });

  // Alex Hanga recommend to move logic into BFF to avoid hacks with unknown
  // by using GET with query params instead of POST with body
  // bff will also reduce overall complexity of this code
  // keep it dirty for now to not block release
  const queryKeyWithRequestData = [
    ...queryKey,
    JSON.stringify(requestData),
  ] as unknown as typeof queryKey;

  const { data, isPending } = useQuery({
    ...queryOptions,
    queryKey: queryKeyWithRequestData,
  });

  const options = useMemo<FlatSelectOption[]>(() => {
    if (!data) {
      return [];
    }

    const mapByPrimaryId = data[relatedFieldId] ?? {};

    const mapByPrimaryValue = Object.values(mapByPrimaryId).reduce(
      (acc, record) => ({ ...acc, ...record }),
      {},
    );

    const relatedFieldsMap = Object.values(mapByPrimaryValue).reduce(
      (acc, record) => ({ ...acc, ...record }),
      {},
    );

    const relatedFieldOptions = Object.entries(relatedFieldsMap).map(
      ([value, label]) => ({ value, label }),
    );

    return relatedFieldOptions;
  }, [data, relatedFieldId]);

  if (isPending) {
    return (
      <div className="flex max-h-75 w-55.5 items-center justify-center bg-ui-primary p-2">
        <ProgressCircular />
      </div>
    );
  }

  return <FlatSelectEditor options={options} {...restProps} />;
};
